import React, { useState, useEffect, useCallback } from 'react';
import Spacing from '../../Spacing';
import SectionHeadingStyle3 from '../../SectionHeading/SectionHeadingStyle3';
import { pageTitle } from '../../../helpers/PageTitle';
import LoadingSpinner from '../../common/LoadingSpinner';
import { toast } from 'react-toastify';

export default function Wishlist() {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncStatus, setSyncStatus] = useState('synced');

  const queueAction = useCallback((action, data) => {
    const pendingActions = JSON.parse(localStorage.getItem('pendingActions') || '[]');
    pendingActions.push({
      id: Date.now(),
      action,
      data,
      timestamp: new Date().toISOString()
    });
    localStorage.setItem('pendingActions', JSON.stringify(pendingActions));
    setSyncStatus('pending');
  }, []);

  const handleRemoveItem = useCallback(async (id, isSync = false) => {
    try {
      if (!isSync && !navigator.onLine) {
        queueAction('remove', { id });
        setWishlistItems(prev => prev.filter(item => item.id !== id));
        localStorage.setItem('wishlist', JSON.stringify(
          wishlistItems.filter(item => item.id !== id)
        ));
        toast.info('Item will be removed when you are back online');
        return;
      }

      await fetch(`/api/wishlist/${id}`, { method: 'DELETE' });
      setWishlistItems(prev => prev.filter(item => item.id !== id));
      toast.success('Item removed from wishlist');
    } catch (err) {
      toast.error('Failed to remove item');
    }
  }, [wishlistItems, queueAction]);

  const handleAddToCart = useCallback(async (item, isSync = false) => {
    try {
      if (!isSync && !navigator.onLine) {
        queueAction('addToCart', item);
        toast.info('Item will be added to cart when you are back online');
        return;
      }

      const response = await fetch('/api/cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item)
      });

      if (!response.ok) throw new Error('Failed to add to cart');
      
      toast.success('Item added to cart');
      await handleRemoveItem(item.id);
    } catch (err) {
      toast.error('Failed to add item to cart');
    }
  }, [handleRemoveItem, queueAction]);

  const syncPendingActions = useCallback(async () => {
    const pendingActions = JSON.parse(localStorage.getItem('pendingActions') || '[]');
    if (pendingActions.length === 0) return;

    setSyncStatus('syncing');
    
    for (const action of pendingActions) {
      try {
        switch (action.action) {
          case 'remove':
            await handleRemoveItem(action.data.id, true);
            break;
          case 'addToCart':
            await handleAddToCart(action.data, true);
            break;
          default:
            break;
        }
      } catch (err) {
        console.error('Failed to sync action:', action, err);
        toast.error('Failed to sync some changes');
      }
    }

    localStorage.removeItem('pendingActions');
    setSyncStatus('synced');
    toast.success('All changes synced successfully');
  }, [handleRemoveItem, handleAddToCart]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncPendingActions();
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      toast.info('Working offline. Changes will sync when you are back online');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncPendingActions]);

  useEffect(() => {
    const loadWishlist = async () => {
      try {
        setLoading(true);
        const cached = localStorage.getItem('wishlist');
        if (cached) {
          setWishlistItems(JSON.parse(cached));
          setLoading(false);
        }

        if (navigator.onLine) {
          const response = await fetch('/api/wishlist');
          if (!response.ok) throw new Error('Failed to fetch wishlist');
          const data = await response.json();
          setWishlistItems(data);
          localStorage.setItem('wishlist', JSON.stringify(data));
        }
      } catch (err) {
        toast.error('Failed to load wishlist items');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadWishlist();
  }, []);

  pageTitle('Wishlist');

  if (loading) {
    return (
      <>
        <Spacing lg="70" md="70" />
        <LoadingSpinner text="Loading wishlist..." />
      </>
    );
  }

  return (
    <>
      <Spacing lg="70" md="70" />
      <Spacing lg="140" md="80" />
      <SectionHeadingStyle3
        title="Your Wishlist"
        subTitle="Shop"
        variant="text-center"
        href="/shop"
      />
      {!isOnline && (
        <div className="cs_offline_indicator">
          Working Offline
        </div>
      )}
      {syncStatus === 'pending' && (
        <div className="cs_sync_pending">
          Changes pending sync
        </div>
      )}
      {syncStatus === 'syncing' && (
        <div className="cs_syncing">
          Syncing changes...
        </div>
      )}
      
      <div className="container">
        {wishlistItems.length > 0 ? (
          <div className="cs_wishlist_items">
            {wishlistItems.map(item => (
              <div key={item.id} className="cs_wishlist_item">
                <img src={item.thumbUrl} alt={item.title} />
                <h3>{item.title}</h3>
                <p>${item.price}</p>
                <button onClick={() => handleAddToCart(item)}>Add to Cart</button>
                <button onClick={() => handleRemoveItem(item.id)}>Remove</button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center">Your wishlist is empty</p>
        )}
      </div>
      <Spacing lg="150" md="80" />
    </>
  );
}
